import * as Select from "@radix-ui/react-select"
import PropTypes from "prop-types"
import { ChevronDown } from "../Icon"
import { cn } from "../../../lib/utils"

/**
 * AppSelect
 *
 * Radix Select — keyboard-navigable, string values.
 *   onChange(value: string)
 *
 * variant="pill": Renders the Radix trigger as a borderless pill chip (source filter style).
 */
export default function AppSelect({
  options = [],
  placeholder = "Select...",
  disabled,
  variant,
  value,
  onChange,
  // testing attributes
  "data-testid": dataTestId,
}) {
  // ── Radix mode ───────────────────────────────────────────────────────────────
  const isPill = variant === "pill"

  const triggerClass = cn(
    // base trigger styles
    "app-select-trigger",
    "flex items-center justify-between gap-2 cursor-pointer",
    "text-[13px] text-[var(--text-body,#334155)]",
    "bg-[var(--surface-card,#fff)]",
    "border border-[var(--border-default,#CBD5E1)]",
    "hover:border-[var(--brand-green,#00875A)]",
    "transition-colors",
    isPill
      ? [
          // pill variant
          "app-select-trigger--pill",
          "h-7 px-[10px]",
          "text-[var(--font-sm)]",
          "rounded-[var(--radius-pill,16px)]",
          "border border-[var(--border-subtle-2,#e2e8f0)]",
          "bg-[var(--color-panel-solid,#ffffff)]",
          "min-w-0",
        ]
      : [
          // default variant
          "px-[10px] py-[6px]",
          "rounded-[var(--radius-sm,4px)]",
          "min-w-[120px]",
        ],
  )

  return (
    <Select.Root value={value} onValueChange={onChange} disabled={disabled}>
      <Select.Trigger className={triggerClass} data-testid={dataTestId}>
        <Select.Value placeholder={placeholder} />
        <Select.Icon>
          <ChevronDown size={14} />
        </Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content
          position="popper"
          sideOffset={4}
          className={cn(
            "app-select-content",
            "bg-[var(--surface-card,#fff)]",
            "border border-[var(--border-subtle-2,#E2E8F0)]",
            "rounded-[var(--radius-sm,4px)]",
            "shadow-[0_4px_16px_rgba(0,0,0,0.12)]",
            "z-[1050]",
            "max-h-[280px] overflow-hidden",
            "w-[var(--radix-select-trigger-width)]",
          )}
        >
          <Select.Viewport className="max-h-[280px] overflow-y-auto p-1">
            {options.map((opt) => (
              <Select.Item
                key={opt.value}
                value={opt.value}
                className={cn(
                  "app-select-item",
                  "px-3 py-2 text-[13px] cursor-pointer",
                  "text-[var(--text-body,#334155)]",
                  "data-[highlighted]:bg-[var(--surface-page,#F1F5F9)]",
                  "data-[highlighted]:outline-none",
                )}
              >
                <Select.ItemText>{opt.label}</Select.ItemText>
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  )
}

AppSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  variant: PropTypes.oneOf(["pill", "default"]),
  value: PropTypes.string,
  onChange: PropTypes.func,
  "data-testid": PropTypes.string,
}
